import { FormControl, IconButton, MenuItem, Select, Slider, Tooltip, Typography, FormGroup, FormControlLabel, Switch } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LoopMode, useVideo } from "../../../../../context/juno/VideoContext";
import { ColumnBox, RowBox } from "../../../../../utils/styledBox";
import AudioUpload from "../AudioUpload";
import ModelVersionSelectBox from "../ModelVersionSelectBox";
import VideoUpload from "../VideoUpload";
import ImageUpload from "../ImageUpload";
import AspectRatioIcons from "../AspectRatioIcons";
import { Info } from "@mui/icons-material";

const ParamsBytedance = () => {
  const { t } = useTranslation();
  const {
    menu,
    modelVersion,
    guidanceScales,
    loopMode,
    setLoopMode,
    video1,
    setVideo1,
    audio1,
    setAudio1,
    audio1Duration,
    setAudio1Duration,
    image1,
    setImage1,
    duration,
    cameraFixed,
    setCameraFixed,
    updateParams,
  } = useVideo();

  // LATENTSYNCモデルかどうか判定
  const isLatentSyncModel = modelVersion.BYTEDANCE === "LATENTSYNC";
  // SEEDANCEモデルかどうか判定
  const isSeedanceModel = modelVersion.BYTEDANCE === "SEEDANCE_V1_LITE";

  // Seedance用のアスペクト比
  const seedanceAspectRatio = ["16:9", "4:3", "1:1", "9:21"];

  // Seedance用のDurationマーク
  const seedanceDurationMarks = [
    {
      value: 5,
      label: "5s",
    },
    {
      value: 10,
      label: "10s",
    },
  ];

  return (
    <ColumnBox sx={{ gap: 4 }}>
      <ColumnBox>
        {/* Model */}
        <Typography variant="body2" mb={1}>
          {t("juno.video.ai.model")}
        </Typography>
        <ModelVersionSelectBox onChange={(value) => updateParams("BYTEDANCE", "modelVersion", value)} />
      </ColumnBox>

      {/* LATENTSYNC用のパラメータ */}
      {isLatentSyncModel && (
        <>
          <ColumnBox>
            <Typography variant="body2" mb={1}>
              {t("juno.video.params.uploadFaceVideo")}
            </Typography>
            <VideoUpload video={video1} setVideo={setVideo1} />
          </ColumnBox>

          <ColumnBox>
            <Typography variant="body2" mb={1}>
              {t("juno.video.params.audio")}
            </Typography>
            <AudioUpload
              audio={audio1}
              setAudio={setAudio1}
              audioDuration={audio1Duration}
              setAudioDuration={setAudio1Duration}
            />
          </ColumnBox>

          <ColumnBox>
            <Typography variant="body2" mb={1}>
              {t("juno.video.params.loopMode")}
            </Typography>
            <FormControl fullWidth>
              <Select value={loopMode} onChange={(e) => setLoopMode(e.target.value as LoopMode)}>
                <MenuItem value="pingpong">{t("juno.video.params.pingpong")}</MenuItem>
                <MenuItem value="loop">{t("juno.video.params.loop")}</MenuItem>
              </Select>
            </FormControl>
          </ColumnBox>

          <ColumnBox>
            <RowBox sx={{ justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" mb={1}>
                {t("juno.video.params.guidanceScale")}
              </Typography>
              <Tooltip title={t("juno.video.params.guidanceScaleHelp")} placement="top">
                <IconButton size="small">
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </RowBox>
            <Slider
              value={guidanceScales["BYTEDANCE"]}
              onChange={(_, value) => updateParams("BYTEDANCE", "guidanceScale", value)}
              min={1}
              max={2}
              step={0.1}
              valueLabelDisplay="auto"
              marks={[
                { value: 1, label: "1" },
                { value: 1.5, label: "1.5" },
                { value: 2, label: "2" },
              ]}
            />
          </ColumnBox>
        </>
      )}

      {/* SEEDANCE用のパラメータ */}
      {isSeedanceModel && (
        <>
          {/* Duration */}
          <ColumnBox>
            <Typography variant="body2" mb={1}>
              {t("juno.video.params.duration")}
            </Typography>
            <Slider
              aria-label="duration"
              marks={seedanceDurationMarks}
              min={5}
              max={10}
              step={5}
              value={duration.BYTEDANCE}
              onChange={(_, value) => updateParams("BYTEDANCE", "duration", value)}
            />
          </ColumnBox>

          {/* Camera Fixed */}
          <ColumnBox>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={cameraFixed} onChange={() => setCameraFixed(!cameraFixed)} />}
                label={t("juno.video.params.cameraFixed")}
              />
            </FormGroup>
          </ColumnBox>

          {/* Image Upload for i2v */}
          {menu === "i2v" && (
            <ColumnBox sx={{ justifyContent: "center" }}>
              <Typography variant="body2" mb={1}>
                {t("juno.video.params.upload")}
              </Typography>
              <ImageUpload image={image1} setImage={setImage1} />
            </ColumnBox>
          )}

          {/* Aspect Ratio */}
          <ColumnBox>
            <AspectRatioIcons aspectRatioList={seedanceAspectRatio} />
          </ColumnBox>
        </>
      )}
    </ColumnBox>
  );
};

export default ParamsBytedance;
